// src/components/DoronHero.jsx
import React, { useEffect, useMemo, useState } from "react";

const ASSET_V = "1";

const DORON_AWAKE_LIST = [
  `/images/DORON.png?v=${ASSET_V}`,
  `/images/Doron.png?v=${ASSET_V}`,
  `/images/doron.png?v=${ASSET_V}`,
  `/images/DORON.PNG?v=${ASSET_V}`,
];

// ✅ IMPORTANT: Put your new correct file FIRST
// public/images/sleeping-doron.png  -> served at /images/sleeping-doron.png
const DORON_SLEEP_LIST = [
  `/images/sleeping-doron.png?v=${ASSET_V}`,

  // older fallbacks (keep, just in case)
  `/images/${encodeURIComponent("sleeping-doron.png")}?v=${ASSET_V}`,
  `/images/Sleeping%20Doron.png?v=${ASSET_V}`,
  `/images/Sleeping Doron.png?v=${ASSET_V}`,
  `/images/sleeping%20doron.png?v=${ASSET_V}`,
];

function SmartImage({ srcList = [], alt = "", className = "" }) {
  const [idx, setIdx] = useState(0);
  const src = srcList[idx] || srcList[0] || "";
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        if (idx < srcList.length - 1) setIdx((p) => p + 1);
      }}
      draggable={false}
    />
  );
}

export default function DoronHero({ isComplete, noteEvent, matchedCount = 0 }) {
  const doronSrcList = useMemo(
    () => (isComplete ? DORON_SLEEP_LIST : DORON_AWAKE_LIST),
    [isComplete]
  );

  // ✅ Wiggle only on CORRECT note (damage)
  const [doWiggle, setDoWiggle] = useState(false);

  // ✅ Zzz bursts on every correct note (matchedCount increases each hit)
  const [zzzBursts, setZzzBursts] = useState([]);

  // ✅ Fire bursts on every WRONG note
  const [fireBursts, setFireBursts] = useState([]);

  // Trigger wiggle on hit (transient)
  useEffect(() => {
    if (isComplete) return;
    if (noteEvent !== "hit") return;

    setDoWiggle(true);
    const t = setTimeout(() => setDoWiggle(false), 520);
    return () => clearTimeout(t);
  }, [noteEvent, isComplete]);

  // Slower Zzz timing (linger longer + rise slower)
  useEffect(() => {
    if (isComplete) return;
    if (noteEvent !== "hit") return;
    if (matchedCount <= 0) return;

    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const dx = Math.round((Math.random() - 0.5) * 40);

    setZzzBursts((prev) => [...prev, { id, dx }]);

    const t = setTimeout(() => {
      setZzzBursts((prev) => prev.filter((x) => x.id !== id));
    }, 2400);

    return () => clearTimeout(t);
  }, [matchedCount, noteEvent, isComplete]);

  // Fire bursts on miss (multiple 🔥)
  useEffect(() => {
    if (isComplete) return;
    if (noteEvent !== "miss") return;

    const makeOne = () => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const dx = Math.round((Math.random() - 0.5) * 80);
      const delay = Math.round(Math.random() * 90);
      const scale = 1.25 + Math.random() * 0.7;
      return { id, dx, delay, scale };
    };

    const burst = Array.from({ length: 5 }, makeOne);
    setFireBursts((prev) => [...prev, ...burst]);

    const t = setTimeout(() => {
      setFireBursts((prev) =>
        prev.filter((x) => !burst.some((b) => b.id === x.id))
      );
    }, 1200);

    return () => clearTimeout(t);
  }, [noteEvent, isComplete]);

  return (
    <div className="relative w-full max-w-4xl rounded-[24px] sm:rounded-[28px] shadow-lg bg-slate-900/5 overflow-visible">
      <style>{`
        @keyframes tt-wiggle-damage {
          0%   { transform: translate(0,0) rotate(0deg) scale(1); }
          15%  { transform: translate(-2px,0px) rotate(-1.2deg) scale(1.01); }
          30%  { transform: translate(3px,1px) rotate(1.5deg) scale(1.01); }
          45%  { transform: translate(-3px,0px) rotate(-1.5deg) scale(1.01); }
          60%  { transform: translate(2px,-1px) rotate(1.0deg) scale(1.005); }
          75%  { transform: translate(-1px,0px) rotate(-0.6deg) scale(1.002); }
          100% { transform: translate(0,0) rotate(0deg) scale(1); }
        }
        .tt-doron-damage {
          animation: tt-wiggle-damage 0.52s ease-out;
          transform-origin: 60% 70%;
        }

        @keyframes tt-zzz-rise {
          0%   { transform: translate(calc(-50% + var(--dx)), 24px) scale(0.95); opacity: 0; }
          18%  { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), -140px) scale(1.18); opacity: 0; }
        }
        .tt-zzz-burst {
          position: absolute;
          left: 58%;
          top: 72%;
          font-weight: 900;
          font-size: 15px;
          color: rgba(30, 64, 175, 0.95);
          text-shadow: 0 3px 10px rgba(2, 6, 23, 0.18);
          animation: tt-zzz-rise 2.25s ease-out forwards;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }

        @keyframes tt-fire-burst {
          0%   { transform: translate(calc(-50% + var(--dx)), 12px) scale(calc(var(--sc) * 0.9)); opacity: 0; filter: blur(0px); }
          12%  { opacity: 1; transform: translate(calc(-50% + var(--dx)), 2px) scale(calc(var(--sc) * 1.08)); }
          70%  { opacity: 0.98; }
          100% { transform: translate(calc(-50% + var(--dx)), -105px) scale(calc(var(--sc) + 0.25)); opacity: 0; filter: blur(0.25px); }
        }
        .tt-fire {
          position: absolute;
          left: 54%;
          top: 64%;
          font-size: 32px;
          animation: tt-fire-burst 0.95s ease-out forwards;
          pointer-events: none;
          user-select: none;
          text-shadow: 0 10px 22px rgba(0,0,0,0.28);
          white-space: nowrap;
        }
      `}</style>

      <img
        src="/images/Doron_Battle_Background.png"
        alt="Treble Mountain – Doron battle background"
        className="w-full h-40 sm:h-52 md:h-60 object-cover"
        draggable={false}
      />

      <div className="absolute inset-0 flex items-end justify-center pb-3 sm:pb-4 md:pb-5">
        <div className="relative">
          {/* 🔥 Fire bursts (wrong note) */}
          {!isComplete &&
            fireBursts.map(({ id, dx, delay, scale }) => (
              <div
                key={id}
                className="tt-fire"
                style={{
                  "--dx": `${dx}px`,
                  "--sc": scale,
                  animationDelay: `${delay}ms`,
                }}
              >
                🔥
              </div>
            ))}

          {/* Zzz bursts (correct note) */}
          {!isComplete &&
            zzzBursts.map(({ id, dx }) => (
              <div
                key={id}
                className="tt-zzz-burst"
                style={{ "--dx": `${dx}px` }}
              >
                Zzz 💤 Zzz
              </div>
            ))}

          {/* ✅ Wiggle only on HIT now */}
          <div className={!isComplete && doWiggle ? "tt-doron-damage" : ""}>
            <SmartImage
              srcList={doronSrcList}
              alt="Doron the Dragon"
              className="max-h-44 sm:max-h-52 md:max-h-60 object-contain drop-shadow-xl"
            />
          </div>

          {/* Sleeping ZZZ when completed */}
          {isComplete && (
            <div className="absolute -top-4 -right-2 sm:-top-4 sm:-right-3">
              <span className="text-lg sm:text-xl md:text-2xl font-extrabold text-slate-800">
                Z Z Z
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
